/**
 * Default naming for a companion parent group.
 *
 * Follows the clipboard sync's long-standing convention ("<name> - Parents",
 * `pymble-flows .../clipboard/parent_group_helpers.py`) so a companion created
 * here is indistinguishable, by name, from the ones staff already recognise in
 * pickers and notice audiences.
 *
 * The truncation is load-bearing rather than cosmetic: `companion_name_short`
 * carries the same 80-char server cap as `name_short`, and the API answers an
 * over-long value with a 422 instead of trimming it. A source preset named at
 * or near the cap would therefore make the checkbox un-submittable with no
 * obvious cause.
 */
import {
  COMPANION_NAME_SUFFIX,
  PRESET_NAME_LONG_MAX,
  PRESET_NAME_SHORT_MAX,
} from "../../types/preset";

function withSuffix(sourceName: string, maxLength: number): string {
  const base = sourceName.trim();
  if (!base) return "";
  const full = `${base}${COMPANION_NAME_SUFFIX}`;
  if (full.length <= maxLength) return full;
  // Trim the BASE, never the suffix - a companion whose name silently lost
  // the word "Parents" is the one shape an operator cannot recognise.
  const room = Math.max(0, maxLength - COMPANION_NAME_SUFFIX.length);
  return `${base.slice(0, room).trimEnd()}${COMPANION_NAME_SUFFIX}`;
}

/** Default `companion_name_short`, capped at the server's 80-char limit. */
export function defaultCompanionNameShort(sourceNameShort: string): string {
  return withSuffix(sourceNameShort, PRESET_NAME_SHORT_MAX);
}

/** Default `companion_name_long`, capped at the server's 240-char limit. */
export function defaultCompanionNameLong(sourceNameLong: string): string {
  return withSuffix(sourceNameLong, PRESET_NAME_LONG_MAX);
}
